import type { ItemFacetBucket } from '@supply-mover-2-0/shared';

/**
 * Row logic for a terms facet in the catalog filter rail (PD-16898), kept out
 * of FilterRail so it can be unit-tested.
 *
 * A terms facet is multi-select: a shopper can tick several manufacturers and
 * the results show items from any of them. The server keeps listing the other
 * values while some are ticked, but a ticked value can still be missing from
 * the returned buckets (another filter leaves it with no items, the search
 * found nothing, or the value sits past the bucket cap). It must still render
 * so the shopper can see it is narrowing the results and untick it.
 */

/** One row of a terms facet. `count` is null when there is no count to show. */
export interface TermsFacetRowModel {
	value: string;
	label: string;
	count: number | null;
	selected: boolean;
}

/** Rows shown while the facet is collapsed (before "Show more"). */
export const COLLAPSED_FACET_ROW_COUNT = 10;

/** A facet with more values than this gets a type-to-search box. */
export const FACET_SEARCH_MIN_ROW_COUNT = 10;

type FacetBucketInputModel = Pick<ItemFacetBucket, 'value' | 'label'> & {
	count: number | null;
};

/**
 * Build the rows for a terms facet: the selected values first, then the rest
 * in bucket order (the server's count order). A selected value the buckets do
 * not include gets a row with no count, after the selected values that do.
 */
export function buildTermsFacetRows(
	buckets: Array<FacetBucketInputModel>,
	selectedValues: Array<string>,
): Array<TermsFacetRowModel> {
	const selected = new Set(selectedValues);
	const selectedRows: Array<TermsFacetRowModel> = [];
	const otherRows: Array<TermsFacetRowModel> = [];
	const seen = new Set<string>();

	for (const bucket of buckets) {
		if (seen.has(bucket.value)) {
			continue;
		}

		seen.add(bucket.value);

		const row: TermsFacetRowModel = {
			value: bucket.value,
			label: bucket.label,
			count: bucket.count,
			selected: selected.has(bucket.value),
		};

		if (row.selected) {
			selectedRows.push(row);
		} else {
			otherRows.push(row);
		}
	}

	for (const value of selectedValues) {
		if (seen.has(value) || value.trim().length === 0) {
			continue;
		}

		seen.add(value);
		selectedRows.push({ value, label: value, count: null, selected: true });
	}

	return [...selectedRows, ...otherRows];
}

/**
 * Pick the rows to render. Selected rows always show. The others are narrowed
 * by the search text (case-insensitive, anywhere in the label), and while the
 * facet is collapsed and not being searched they are cut so the collapsed
 * list holds COLLAPSED_FACET_ROW_COUNT rows in total.
 *
 * `hiddenCount` is how many rows the collapse cut, which decides whether the
 * "Show more" toggle is needed.
 */
export function selectVisibleTermsFacetRows(
	rows: Array<TermsFacetRowModel>,
	options: {
		expanded: boolean;
		searchText: string;
		collapsedCount?: number;
	},
): { visible: Array<TermsFacetRowModel>; hiddenCount: number } {
	const collapsedCount = options.collapsedCount ?? COLLAPSED_FACET_ROW_COUNT;
	const needle = options.searchText.trim().toLowerCase();
	const selectedRows = rows.filter((row) => row.selected);
	const matchingOthers = rows.filter(
		(row) =>
			!row.selected &&
			(needle.length === 0 || row.label.toLowerCase().includes(needle)),
	);

	if (options.expanded || needle.length > 0) {
		return { visible: [...selectedRows, ...matchingOthers], hiddenCount: 0 };
	}

	const room = Math.max(collapsedCount - selectedRows.length, 0);

	return {
		visible: [...selectedRows, ...matchingOthers.slice(0, room)],
		hiddenCount: Math.max(matchingOthers.length - room, 0),
	};
}
